'use client';

import { createContext, ReactNode, useCallback, useContext } from "react";
import {
    useSingleTypeEditor,
    UseSingleTypeEditorType
} from "@/app/lib/hooks/use-editor/useSingleTypeEditor";
import { Exhibition, ExhibitionInput, PublicationStatus } from "@/app/lib/generated/graphql-types";
import FloatingEditorMenu from "@/app/features/shared/__to_be_integrated/FloatingEditorMenu";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import { toggleDraftMode } from "@/app/lib/actions/editing";
import { getExhibitionDetail } from "@/app/features/sites/exhibitions/data-access/queries";
import { ExhibitionDetailSchema } from "@/app/features/sites/exhibitions/types/schema";
import { ExhibitionDetailEditable } from "@/app/features/sites/exhibitions/types/types";
import { updateExhibitionDetailInput } from "@/app/features/sites/exhibitions/data-access/mutations";
import {
    exhibitionDetailEditableFrom,
    exhibitionInputFromEditable,
} from "@/app/features/sites/exhibitions/types/mapper";

const SiteExhibitionDetailEditorContext = createContext<UseSingleTypeEditorType<ExhibitionDetailEditable> | undefined>(undefined);

export function useSiteExhibitionDetailEditor() {
    const context = useContext(SiteExhibitionDetailEditorContext);
    if (!context) {
        throw new Error("useSiteExhibitionDetailEditor must be used within a SiteExhibitionDetailEditorProvider");
    }
    return context;
}

interface SiteExhibitionDetailEditorProviderProps {
    children: ReactNode;
    documentId: string;
    isDraftModeEnabled: boolean;
}

export default function SiteExhibitionDetailEditorProvider({
    children,
    documentId,
    isDraftModeEnabled,
}: SiteExhibitionDetailEditorProviderProps) {
    const loadEntity = useCallback(
        ({ status }: { status: PublicationStatus }) => getExhibitionDetail({ documentId, status }),
        [documentId]
    );

    const update = useCallback(
        ({ data, status }: { data: ExhibitionInput; status: PublicationStatus }) => updateExhibitionDetailInput({ documentId, data, status }),
        [documentId]
    );

    const toEditable = useCallback(
        (entity: Exhibition) => exhibitionDetailEditableFrom(entity),
        []
    );

    const toInput = useCallback(
        (editable: ExhibitionDetailEditable) => exhibitionInputFromEditable(editable),
        []
    );

    const editor = useSingleTypeEditor<Exhibition, ExhibitionDetailEditable, ExhibitionInput>({
        schema: ExhibitionDetailSchema,
        loadEntity,
        update,
        toEditable,
        toInput,
    });

    if (editor.isLoading) return <LoadingSpinner />;

    return (
        <SiteExhibitionDetailEditorContext.Provider value={editor}>
            {children}
            <FloatingEditorMenu
                autosaveState={editor.autosaveState}
                isDraftModeEnabled={isDraftModeEnabled}
                toggleDraftMode={toggleDraftMode}
                hasChanges={editor.hasChanges}
                onPublish={editor.publish}
                onDiscard={editor.discardChanges}
            />
        </SiteExhibitionDetailEditorContext.Provider>
    )
}
